import { Box, Tooltip } from '@mui/material';
import type { ReactNode } from 'react';

/**
 * The class a month column sets on its cells so the button below can fill them.
 *
 * `LiftAITable` bans `sx`, so the DataGrid's cell padding is stripped from a wrapper
 * instead. Without it the button is held to the cell's content box and the grid draws
 * an ellipsis over a two-digit count.
 */
export const MONTH_CELL_CLASS = 'service-month-cell';

export const MONTH_CELL_SX = {
  [`& .MuiDataGrid-cell.${MONTH_CELL_CLASS}`]: {
    px: 0.25,
    py: 0,
    overflow: 'visible',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
} as const;

const CELL_HEIGHT = 34;

/**
 * One surface for both grids, so a cell looks like a cell whichever sub-tab is open.
 * What it means is carried by its contents, not by its background.
 */
const CELL_SURFACE = 'action.hover';

/**
 * One clickable month in a service-history grid. Filled to the column width, because a
 * bare glyph gave no hint that the cell opens anything.
 */
export function MonthCellButton({
  title,
  label,
  onClick,
  color,
  dimmed = false,
  dataAttributes,
  children,
}: {
  title: string;
  label: string;
  onClick: () => void;
  color?: string;
  /** A period reaching outside the chosen window. */
  dimmed?: boolean;
  dataAttributes?: Record<string, string>;
  children: ReactNode;
}) {
  return (
    <Tooltip title={title}>
      <Box
        component="button"
        type="button"
        aria-label={label}
        {...dataAttributes}
        onClick={(event) => {
          event.stopPropagation();
          onClick();
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.25,
          width: '100%',
          height: CELL_HEIGHT,
          p: 0,
          borderRadius: 1,
          border: '1px solid transparent',
          cursor: 'pointer',
          bgcolor: CELL_SURFACE,
          color,
          fontSize: 13,
          fontWeight: 500,
          lineHeight: 1,
          opacity: dimmed ? 0.55 : 1,
          transition: 'border-color 120ms, filter 120ms',
          '&:hover': { borderColor: 'text.primary', filter: 'brightness(0.94)' },
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
        }}
      >
        {children}
      </Box>
    </Tooltip>
  );
}
